<script setup>
import { ref, onMounted } from 'vue';
import Button from 'dashboard/components-next/button/Button.vue';
import twentyApi from 'dashboard/api/integrations/twenty';
import CreateOrLinkPerson from './CreateOrLinkPerson.vue';

const props = defineProps({
  conversationId: { type: [Number, String], required: true },
  contactName: { type: String, default: '' },
  contactEmail: { type: String, default: '' },
  contactPhone: { type: String, default: '' },
});

const emit = defineEmits(['linked', 'unlinked']);

const TWENTY_LOGO = '/dashboard/images/integrations/twenty.png';

const linked = ref(null);
const instanceUrl = ref('');
const isLoading = ref(false);
const shouldShowModal = ref(false);

const fetchLinked = async () => {
  isLoading.value = true;
  try {
    const { data } = await twentyApi.getLinkedRecords(props.conversationId);
    linked.value =
      (data?.data || []).find(r => r.twenty_record_type === 'person') || null;
    instanceUrl.value = data?.instance_url || '';
  } finally {
    isLoading.value = false;
  }
};

onMounted(fetchLinked);

const openRecord = () => {
  if (!linked.value || !instanceUrl.value) return;
  window.open(
    `${instanceUrl.value}/object/person/${linked.value.twenty_record_id}`,
    '_blank'
  );
};

const unlink = async () => {
  if (!linked.value) return;
  await twentyApi.unlinkRecord(linked.value.id);
  linked.value = null;
  emit('unlinked');
};

const onLinked = async () => {
  shouldShowModal.value = false;
  await fetchLinked();
  emit('linked');
};
</script>

<template>
  <div class="flex flex-col gap-2">
    <p class="text-xs font-semibold text-n-slate-11 uppercase tracking-wide">
      {{ $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.TITLE') }}
    </p>

    <!-- Loading -->
    <div v-if="isLoading" class="text-xs text-n-slate-11">
      {{ $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.LOADING') }}
    </div>

    <!-- Linked person card -->
    <template v-else-if="linked">
      <div class="flex flex-col gap-4">
        <div
          class="flex flex-col sm:flex-row items-start sm:items-center justify-between gap-2"
        >
          <div
            class="flex items-center gap-2 px-2 py-1.5 border rounded-lg border-n-strong flex-1 min-w-0"
          >
            <div class="flex items-center gap-1 min-w-0 flex-1">
              <img :src="TWENTY_LOGO" class="size-4 shrink-0" alt="Twenty" />
              <span class="text-xs font-medium text-n-slate-12 truncate">
                {{ linked.twenty_record_name }}
              </span>
            </div>
            <span class="w-px h-3 text-n-weak bg-n-weak shrink-0" />
            <Button
              link
              xs
              slate
              icon="i-lucide-arrow-up-right"
              class="!size-4 shrink-0"
              @click="openRecord"
            />
          </div>
          <Button
            ghost
            xs
            slate
            icon="i-lucide-unlink"
            class="self-end sm:self-auto shrink-0"
            @click="unlink"
          />
        </div>
      </div>
    </template>

    <!-- Idle: no person linked -->
    <template v-else>
      <Button
        ghost
        xs
        icon="i-lucide-plus"
        :label="$t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.ADD_RECORD')"
        @click="shouldShowModal = true"
      />
      <p class="text-xs text-n-slate-11 text-center py-2">
        {{ $t('INTEGRATION_SETTINGS.TWENTY.SIDEBAR.PERSON.EMPTY') }}
      </p>
    </template>

    <!-- Modal -->
    <woot-modal
      v-model:show="shouldShowModal"
      :on-close="() => (shouldShowModal = false)"
      :close-on-backdrop-click="false"
      class="!items-start [&>div]:!top-12 [&>div]:sticky"
    >
      <CreateOrLinkPerson
        v-if="shouldShowModal"
        :conversation-id="conversationId"
        :contact-name="contactName"
        :contact-email="contactEmail"
        :contact-phone="contactPhone"
        :instance-url="instanceUrl"
        @linked="onLinked"
        @close="shouldShowModal = false"
      />
    </woot-modal>
  </div>
</template>
